import React, { useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { DisplayText, Modal } from '@superops/ui-design-system';
import ClientUploader from '@nile/components/src/DromoUploader/Uploaders/ClientUploader';
import ClientContractV3Uploader from '@nile/components/src/DromoUploader/Uploaders/ClientContractV3Uploader';
import RequesterUploader from '@nile/components/src/DromoUploader/Uploaders/RequesterUploader';
import SiteUploader from '@nile/components/src/DromoUploader/Uploaders/SiteUploader';
import ClientContractUploader from '@nile/components/src/DromoUploader/Uploaders/ClientContractUploader';
import ServiceItemUploader from '@nile/components/src/DromoUploader/Uploaders/ServiceItemUploader';
import TechniciansGroupsUploader from '@nile/components/src/DromoUploader/Uploaders/TechniciansGroupsUploader';
import TicketUploader from '@nile/components/src/DromoUploader/Uploaders/TicketUploader';
import TicketConversationsUploader from '@nile/components/src/DromoUploader/Uploaders/TicketConversationsUploader';
import TicketNotesUploader from '@nile/components/src/DromoUploader/Uploaders/TicketNotesUploader';
import TicketWorklogsUploader from '@nile/components/src/DromoUploader/Uploaders/TicketWorklogsUploader';
import UnmonitorAssetDromoUploadModal from '@nile/rmm/src/modules/home/components/sub-pages/rmm-toolbar/UnmonitorAssetDromoUploadModal';
import TechnicianUploader from '@nile/components/src/DromoUploader/Uploaders/TechnicianUploader';
import {
  getTranslatedText,
  isFeatureAuthorisedInPlan,
  useActivityTracker,
  useIsAuthorisedHook
} from '@nile/components';

import { GET_CUSTOMER_IDENTITY_TOKEN } from '@nile/components/src/DromoUploader/dromo';
import { AccountClient } from '@nile/graphql';

const CSVImportButton = ({
  type,
  onImportComplete,
  onDromoStateChange,
  uiType = 'default',
  children
}) => {
  const intl = useIntl();
  const isNewContracts = isFeatureAuthorisedInPlan('CONTRACT_V3');

  const importSheets = {
    CLIENT: {
      name: 'CLIENT',
      component: ClientUploader,
      show: useIsAuthorisedHook('client.add'),
      customValidation: true,
      customFields: true
    },
    REQUESTER: {
      name: 'REQUESTER',
      component: RequesterUploader,
      show: useIsAuthorisedHook('users.add'),
      customFields: true
    },
    COMPANY_USER: {
      name: 'COMPANY_USER',
      component: TechnicianUploader
    },
    SITE: {
      name: 'SITE',
      component: SiteUploader,
      show: useIsAuthorisedHook('site.add')
    },
    CLIENTCONTRACT: {
      name: 'CLIENTCONTRACT',
      component: isNewContracts
        ? ClientContractV3Uploader
        : ClientContractUploader,
      show: useIsAuthorisedHook('contract.add')
    },
    SERVICEITEM: {
      name: 'SERVICEITEM',
      component: ServiceItemUploader,
      show: useIsAuthorisedHook('users.add')
    },
    TECHNICIANSGROUPS: {
      name: 'TECHNICIANSGROUPS',
      component: TechniciansGroupsUploader
    },
    TICKET: {
      name: 'TICKET',
      component: TicketUploader
    },
    TICKETCONVERSATIONS: {
      name: 'TICKETCONVERSATIONS',
      component: TicketConversationsUploader
    },
    TICKETNOTES: {
      name: 'TICKETNOTES',
      component: TicketNotesUploader
    },
    TICKETWORKLOGS: {
      name: 'TICKETWORKLOGS',
      component: TicketWorklogsUploader
    },
    UNMONITOR_ASSET: {
      name: 'UNMONITOR_ASSET',
      component: UnmonitorAssetDromoUploadModal,
      show: useIsAuthorisedHook('agentDownload.allow')
    }
  };
  const [active, setActive] = useState(false);
  const [fields, setFields] = useState({});
  const [validationData, setValidationData] = useState({});
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Customer identity token
  const { data: token, loading: tokenLoading, error: tokenError } = useQuery(
    GET_CUSTOMER_IDENTITY_TOKEN,
    {
      fetchPolicy: 'network-only',
      client: AccountClient
    }
  );

  const fieldsRef = useRef();
  fieldsRef.current = fields;

  const isImportingRef = useRef(false);

  const toggleActive = () => setActive(!active);

  const toggleConfirmationActive = () => setConfirmationOpen(!confirmationOpen);

  function trackClickEvent() {
    useActivityTracker({ event: `migrate_page_import_${type}` });
  }

  const Sheet = importSheets[type];
  const Component = importSheets[type].component;

  const handleActivatorClick = (e) => {
    // Use ref for immediate check (state updates are async)
    if (isImportingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    // Set both ref (immediate) and state (for UI updates)
    isImportingRef.current = true;
    setIsImporting(true);
    // Notify parent component about state change
    if (onDromoStateChange) {
      onDromoStateChange(true);
    }
    if (uiType !== 'dropdown') {
      trackClickEvent();
    }
  };

  const getActivator = () => {
    const commonProps = {
      onClick: handleActivatorClick,
      onClickCapture: (e) => {
        if (isImportingRef.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    if (uiType === 'dropdown') {
      return (
        <div {...commonProps} className={isImporting ? 'disabled' : ''}>
          {children || <div />}
        </div>
      );
    }

    return (
      <div {...commonProps} className={isImporting ? 'disabled' : ''}>
        <SVGIcon name="CsvImport" size="xl" />
        <DisplayText size="small" weight="Medium">
          <FormattedMessage id="dromo.csv.import" />
        </DisplayText>
      </div>
    );
  };

  return (
    <>
      <Modal
        open={confirmationOpen}
        onClose={toggleConfirmationActive}
        variant="confirmation"
        primaryAction={{
          content: getTranslatedText(intl, 'dromo.confirmation.okay'),
          onAction: toggleConfirmationActive
        }}
        showCloseButton
        small
      >
        <Modal.Section>
          <FormattedMessage id="dromo.import.submit.notification" />
        </Modal.Section>
      </Modal>
      <Component
        intl={intl}
        activator={getActivator()}
        onResults={() => {
          toggleActive();
          toggleConfirmationActive();
          isImportingRef.current = false;
          setIsImporting(false);
          if (onDromoStateChange) {
            onDromoStateChange(false);
          }
          onImportComplete();
        }}
        onCancel={() => {
          toggleActive();
          isImportingRef.current = false;
          setIsImporting(false);
          if (onDromoStateChange) {
            onDromoStateChange(false);
          }
        }}
        fields={fieldsRef.current?.[Sheet.name]}
        validationProps={validationData[Sheet.name]}
        userToken={token?.getCustomerIdentityToken}
      />
    </>
  );
};

export default CSVImportButton;
